import {useCallback} from 'react';

import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {
    getUserCommonLimits,
    removeUserCommonLimit,
    saveUserCommonLimit,
} from 'src/openapi/admin/apis/UserCommonLimitsApi';
import {useAppDispatch} from 'src/store/hooks';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import type {SaveOptions} from 'src/components/save-with-reason/save-with-reason-button';
import type {SaveContext} from 'src/hooks/use-common-data-hook';
import type {Limit} from 'src/openapi/admin/models/Limit';
import type {ErrorPayload, SuccessPayload} from 'src/typedef';

type Props = {
    userId: number,
};

export const useUserCommonLimits = ({userId}: Props) => {
    const apiDispatch = useAppDispatch();

    const getDispatchAction = useCallback((signal: AbortSignal) => apiDispatch(
        getUserCommonLimits({userId}, {signal}),
    ), [userId, apiDispatch]);

    const saveDispatchAction = useCallback(async (
        limits: Limit[],
        signal: AbortSignal,
        extra: (SaveOptions & {initialLimits?: Limit[]}) | undefined,
        {initialData, setInitialData}: SaveContext<Limit[]>,
    ) => {
        const {initialLimits, reason} = extra || {};
        if (!initialLimits) {
            return Promise.resolve({type: '', payload: {
                data: {message: 'No initial limits found'},
                success: false,
                error: true,
            } as ErrorPayload});
        }

        const removedLimits = initialData.filter(initialLimit => (
            !limits.find(limit => limit.limitId === initialLimit.limitId)
        ));

        const savedLimits = initialData.filter(initial => limits.some(limit => limit.limitId === initial.limitId));
        if (!isDeepEqual({prev: savedLimits, current: limits})) {
            const saveCommonLimitsResponse = await apiDispatch(saveUserCommonLimit({
                userId,
                userLimitsSaveRequest: {limits, reason},
            }, {signal}));
            if (saveCommonLimitsResponse.payload.error) {
                return Promise.resolve({type: '', payload: saveCommonLimitsResponse.payload});
            }
            setInitialData([...limits, ...removedLimits]);
        }

        if (removedLimits.length) {
            const removeCommonLimitsResponse = await apiDispatch(removeUserCommonLimit({
                userId,
                userLimitsRemoveRequest: {limitIds: removedLimits.map(({limitId}) => limitId), reason},
            }, {signal}));
            if (removeCommonLimitsResponse.payload.error) {
                return Promise.resolve({type: '', payload: removeCommonLimitsResponse.payload});
            }
        }

        return Promise.resolve({type: '', payload: {
            data: undefined,
            success: true,
            error: false,
        } as SuccessPayload<void>});
    }, [userId, apiDispatch]);

    const hook = useCommonDataHook({
        initValue: [],
        getDispatchAction,
        saveDispatchAction,
    });
    const {saveChanges: save} = hook;
    const saveChanges = useCallback((initialLimits?: Limit[], options?: SaveOptions) => (
        save({initialLimits, ...options})
    ), [save]);

    return {...hook, saveChanges};
};
